import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Users, Plus, LogIn, Copy, Crown, Hash, Swords, Play, Loader2, BookCheck, Save, Trash2, ArrowLeft, X, Trophy } from "lucide-react";
import { apiClient } from "../lib/api";
import { useAuth } from "../lib/auth";
import useSubjectTopics from "../hooks/useSubjectTopics";
import LeaveConfirmModal from "../components/LeaveConfirmModal";

const GRADES = ["Grade 9", "Grade 10", "Grade 11", "Grade 12"];
const SUBJECTS = ["Math", "Physics", "Chemistry", "Biology", "English", "History", "Geography"];

/** Safely flatten backend error detail to a string. */
function safeDetail(e) {
  const d = e?.response?.data?.detail;
  if (!d) return null;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" · ");
  try { return JSON.stringify(d); } catch { return null; }
}

export default function Private() {
  const nav = useNavigate();
  const { code } = useParams();
  const { user } = useAuth();
  const [tab, setTab] = useState("home");   // home | create | join | builder | lobby

  useEffect(() => { if (code) setTab("lobby"); }, [code]);

  if (!user) {
    return (
      <div className="min-h-[80vh] grid place-items-center">
        <div className="sm-card p-8 text-center">
          <div className="font-mono text-[10px] tracking-[0.3em] text-soft">PRIVATE MODE</div>
          <div className="font-heading font-black text-2xl mt-2">Sign in to create private lobbies</div>
          <button onClick={() => nav("/login")} className="sm-btn-primary px-5 py-3 mt-5">SIGN IN</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
      {tab !== "lobby" && (
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <Pill active={tab === "home"}    onClick={() => setTab("home")}    icon={<Users size={12} />}     label="Home" />
          <Pill active={tab === "create"}  onClick={() => setTab("create")}  icon={<Plus size={12} />}      label="Create Lobby" testid="private-tab-create" />
          <Pill active={tab === "join"}    onClick={() => setTab("join")}    icon={<LogIn size={12} />}     label="Join by Code" testid="private-tab-join" />
          <Pill active={tab === "builder"} onClick={() => setTab("builder")} icon={<BookCheck size={12} />} label="Question Set" testid="private-tab-builder" />
        </div>
      )}
      {tab === "home" && <PrivateHome onCreate={() => setTab("create")} onJoin={() => setTab("join")} onBuild={() => setTab("builder")} />}
      {tab === "create" && <CreateLobby onCreated={(c) => nav(`/private/${c}`)} />}
      {tab === "join" && <JoinLobby onJoined={(c) => nav(`/private/${c}`)} />}
      {tab === "builder" && <CustomQuestionsBuilder onDone={() => setTab("home")} />}
      {tab === "lobby" && code && <LobbyView code={code} onLeave={() => { setTab("home"); nav("/private"); }} />}
    </div>
  );
}

function Pill({ active, onClick, icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg font-mono text-[11px] uppercase tracking-[0.2em] border ${
        active ? "border-blue-500/55 bg-blue-500/15 text-blue" : "border-white/10 text-soft hover:text-white hover:bg-white/5"
      }`}
      data-testid={testid}
    >
      {icon}{label}
    </button>
  );
}

function PrivateHome({ onCreate, onJoin, onBuild }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Card icon={<Plus size={28} className="text-blue" />}  title="Create Lobby" body="Set subject, grade, max players. Share the 6-character code with friends." cta="CREATE" onClick={onCreate} />
      <Card icon={<LogIn size={28} className="text-purple" />} title="Join Lobby"   body="Enter a lobby code to join a private tournament with friends."               cta="JOIN"   onClick={onJoin} />
      <Card icon={<BookCheck size={28} className="text-teal-400" />} title="Question Set" body="Author a custom question set. Use it in any private match you create." cta="BUILD"  onClick={onBuild} />
    </div>
  );
}
function Card({ icon, title, body, cta, onClick }) {
  return (
    <button onClick={onClick} className="sm-card p-6 text-left hover:bg-white/[0.03] transition-colors">
      <div>{icon}</div>
      <div className="font-heading font-black text-xl mt-3">{title}</div>
      <div className="text-soft text-sm mt-2">{body}</div>
      <div className="font-mono text-[10px] tracking-[0.3em] text-blue mt-4">{cta} →</div>
    </button>
  );
}

function CreateLobby({ onCreated }) {
  const [name, setName] = useState("My Lobby");
  const [subject, setSubject] = useState("Math");
  const [topic, setTopic] = useState("");
  const [grade, setGrade] = useState("Grade 10");
  const [maxPlayers, setMaxPlayers] = useState(8);
  const [rounds, setRounds] = useState(10);
  const [roundSeconds, setRoundSeconds] = useState(120);
  const [customTime, setCustomTime] = useState(false);
  const [busy, setBusy] = useState(false);
  const { topics, loading: topicsLoading } = useSubjectTopics(subject, grade);
  const [customCount, setCustomCount] = useState(null);

  // Look up how many of the host's custom questions match the current
  // subject/topic/grade selection so they know how many slots will be filled
  // from their own set vs. auto-filled from the DB.
  useEffect(() => {
    let cancelled = false;
    apiClient.get("/custom/questions")
      .then((r) => {
        if (cancelled) return;
        const arr = r.data?.questions || [];
        const match = arr.filter((q) =>
          (!subject || q.subject === subject) &&
          (!topic || q.topic === topic) &&
          (!grade || q.grade === grade)
        );
        setCustomCount(match.length);
      })
      .catch(() => { if (!cancelled) setCustomCount(0); });
    return () => { cancelled = true; };
  }, [subject, topic, grade]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!name.trim()) { toast.error("Lobby name required"); return; }
    setBusy(true);
    try {
      const r = await apiClient.post("/private/create", {
        name: name.trim(), subject, topic: topic || null, grade,
        max_players: maxPlayers, rounds, mode: "tournament",
        round_seconds: roundSeconds,
      });
      toast.success(`Lobby created: ${r.data.code}`);
      onCreated(r.data.code);
    } catch (e) { toast.error(safeDetail(e) || "Could not create lobby"); }
    finally { setBusy(false); }
  };

  const cc = customCount ?? 0;
  const fromCustom = Math.min(cc, rounds);
  const fromDb = Math.max(0, rounds - fromCustom);

  return (
    <form onSubmit={submit} className="sm-card p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl">
      <div className="sm:col-span-2">
        <div className="font-mono text-[10px] tracking-[0.3em] text-blue uppercase">Create Lobby</div>
        <h3 className="font-heading font-black text-2xl mt-1">Configure your match</h3>
      </div>
      <Field label="Lobby Name"><input className="sm-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={32} data-testid="create-lobby-name" /></Field>
      <Field label="Subject">
        <select className="sm-input" value={subject} onChange={(e) => { setSubject(e.target.value); setTopic(""); }} data-testid="create-lobby-subject">
          {SUBJECTS.map((s) => <option key={s}>{s}</option>)}
        </select>
      </Field>
      <Field label={`Topic ${topicsLoading ? "(loading…)" : `(${topics.length} available)`}`}>
        <select className="sm-input" value={topic} onChange={(e) => setTopic(e.target.value)} data-testid="create-lobby-topic">
          <option value="">Any topic</option>
          {topics.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
      <Field label="Grade">
        <select className="sm-input" value={grade} onChange={(e) => { setGrade(e.target.value); setTopic(""); }} data-testid="create-lobby-grade">
          {GRADES.map((g) => <option key={g}>{g}</option>)}
        </select>
      </Field>
      <Field label="Max Players">
        <input type="number" min="2" max="8" className="sm-input" value={maxPlayers} onChange={(e) => setMaxPlayers(Number(e.target.value))} data-testid="create-lobby-max" />
      </Field>
      <Field label="Rounds per Match">
        <input type="number" min="5" max="20" className="sm-input" value={rounds} onChange={(e) => setRounds(Number(e.target.value))} data-testid="create-lobby-rounds" />
      </Field>
      <Field label="Time per Question">
        <select
          className="sm-input"
          value={customTime ? "custom" : String(roundSeconds)}
          onChange={(e) => {
            if (e.target.value === "custom") { setCustomTime(true); }
            else { setCustomTime(false); setRoundSeconds(Number(e.target.value)); }
          }}
          data-testid="create-lobby-time"
        >
          {[30, 60, 90, 120, 180].map((s) => <option key={s} value={s}>{s} seconds</option>)}
          <option value="custom">Custom…</option>
        </select>
      </Field>
      {customTime && (
        <Field label="Custom seconds (10–600)">
          <input
            type="number" min="10" max="600" className="sm-input"
            value={roundSeconds}
            onChange={(e) => setRoundSeconds(Math.max(10, Math.min(600, Number(e.target.value) || 0)))}
            data-testid="create-lobby-time-custom"
          />
        </Field>
      )}

      {/* Hybrid custom + DB question summary */}
      <div className="sm:col-span-2 rounded-xl border border-blue-500/30 bg-blue-500/[0.06] p-4" data-testid="custom-hybrid-summary">
        <div className="font-mono text-[10px] tracking-[0.3em] text-blue uppercase">Question Pool · Preview</div>
        <div className="mt-2 text-sm text-soft">
          You have <span className="text-blue font-bold">{cc}</span> custom question{cc === 1 ? "" : "s"} matching{" "}
          <span className="text-white font-medium">{subject}{topic ? ` · ${topic}` : ""} · {grade}</span>.
        </div>
        <div className="mt-1 text-sm">
          <span className="text-emerald-400 font-bold">{fromCustom}</span> from your custom set
          {" · "}
          <span className="text-blue font-bold">{fromDb}</span> auto-filled from database
          {" "}<span className="text-faint">(total {rounds} rounds)</span>
        </div>
        {fromCustom === 0 && (
          <div className="mt-2 text-[11px] text-faint">
            Tip: Use the <span className="text-white">Question Set</span> tab to author custom questions for this subject/topic/grade — they'll be used first in your matches.
          </div>
        )}
      </div>

      <div className="sm:col-span-2 flex justify-end">
        <button type="submit" disabled={busy} className="sm-btn-primary px-6 py-3 flex items-center gap-2 disabled:opacity-50" data-testid="create-lobby-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus size={14} />} CREATE LOBBY
        </button>
      </div>
    </form>
  );
}

function JoinLobby({ onJoined }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    const c = (code || "").trim().toUpperCase();
    if (c.length !== 6) { toast.error("Lobby code is 6 characters"); return; }
    setBusy(true);
    try {
      await apiClient.post(`/private/join/${c}`);
      toast.success("Joined lobby");
      onJoined(c);
    } catch (e) { toast.error(safeDetail(e) || "Could not join lobby"); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="sm-card p-6 sm:p-8 max-w-md">
      <div className="font-mono text-[10px] tracking-[0.3em] text-blue uppercase">Join Lobby</div>
      <h3 className="font-heading font-black text-2xl mt-1">Enter Lobby Code</h3>
      <div className="mt-6">
        <input
          className="sm-input text-center tracking-[0.5em] uppercase text-2xl font-heading font-black"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 6))}
          placeholder="ABC123"
          maxLength={6}
          data-testid="join-lobby-code"
        />
      </div>
      <button type="submit" disabled={busy || code.length !== 6} className="sm-btn-primary px-5 py-3 mt-5 w-full flex items-center justify-center gap-2 disabled:opacity-50" data-testid="join-lobby-submit">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn size={14} />} JOIN
      </button>
    </form>
  );
}

function LobbyView({ code, onLeave }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [lobby, setLobby] = useState(null);
  const [busy, setBusy] = useState(false);
  const [kickTarget, setKickTarget] = useState(null);

  const fetchLobby = useCallback(async () => {
    try {
      const r = await apiClient.get(`/private/${code}`);
      const lb = r.data.lobby;
      // If the host removed me, bounce to the home screen.
      if (user && Array.isArray(lb?.players) && !lb.players.some((p) => p.id === user.id)) {
        toast.error("You were removed from the lobby");
        onLeave?.();
        return;
      }
      setLobby(lb);
    }
    catch (e) { toast.error("Lobby not found"); onLeave?.(); }
  }, [code, onLeave, user]);

  const doKick = async () => {
    if (!kickTarget) return;
    try {
      await apiClient.post(`/private/${code}/kick`, { player_id: kickTarget.id });
      toast.success(`Removed ${kickTarget.username}`);
      fetchLobby();
    } catch (e) { toast.error(safeDetail(e) || "Could not remove player"); }
    finally { setKickTarget(null); }
  };

  useEffect(() => {
    fetchLobby();
    const t = setInterval(fetchLobby, 2200);
    return () => clearInterval(t);
  }, [fetchLobby]);

  const isHost = lobby?.host_id === user?.id;

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(code); toast.success("Code copied"); }
    catch { toast.error("Could not copy"); }
  };

  const makeBracket = async () => {
    setBusy(true);
    try { await apiClient.post(`/private/${code}/bracket`); toast.success("Bracket generated"); fetchLobby(); }
    catch (e) { toast.error(safeDetail(e) || "Could not generate bracket"); }
    finally { setBusy(false); }
  };

  const startPair = async (idx) => {
    setBusy(true);
    try {
      const r = await apiClient.post(`/private/${code}/start?bracket_idx=${idx}`);
      if (r.data?.bye) { toast.success(`${r.data.winner} advances (bye)`); fetchLobby(); return; }
      if (r.data?.match_id) {
        toast.success("Match started!");
        nav(`/private/match/${r.data.match_id}`, { state: { code } });
      } else { toast.info("Could not start match"); }
    } catch (e) { toast.error(safeDetail(e) || "Could not start match"); }
    finally { setBusy(false); }
  };

  // Auto-enter: if there's a bracket pair I'm in that is currently playing, jump to it.
  useEffect(() => {
    if (!lobby || !user) return;
    const mine = (lobby.bracket || []).find(
      (p) => p.status === "playing" && p.match_id && (p.a_id === user.id || p.b_id === user.id)
    );
    if (mine) {
      nav(`/private/match/${mine.match_id}`, { state: { code }, replace: false });
    }
  }, [lobby, user, code, nav]);

  if (!lobby) {
    return (
      <div className="min-h-[60vh] grid place-items-center"><Loader2 className="w-10 h-10 text-blue animate-spin" /></div>
    );
  }

  const bracket = lobby.bracket || [];
  const byRound = {};
  bracket.forEach((mu) => { const r = mu.round || 1; (byRound[r] = byRound[r] || []).push(mu); });
  const roundNos = Object.keys(byRound).map(Number).sort((a, b) => a - b);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button onClick={onLeave} className="sm-btn-outline px-4 py-2 flex items-center gap-2 text-xs" data-testid="lobby-back"><ArrowLeft size={14} /> BACK</button>
        <div className="flex items-center gap-3">
          <div className="font-mono text-[10px] tracking-[0.3em] text-soft uppercase flex items-center gap-1"><Hash size={12} /> CODE</div>
          <button onClick={copyCode} className="font-heading font-black text-3xl tracking-[0.4em] text-blue hover:text-cyan" data-testid="lobby-code">{code}</button>
          <button onClick={copyCode} className="sm-btn-outline px-2 py-1 text-xs" data-testid="lobby-copy"><Copy size={12} /></button>
        </div>
      </div>

      <div className="sm-card p-6">
        <div className="font-mono text-[10px] tracking-[0.3em] text-blue uppercase">Lobby</div>
        <h3 className="font-heading font-black text-2xl mt-1">{lobby.name}</h3>
        <div className="text-soft text-sm mt-1">{lobby.subject} · {lobby.topic || "Any topic"} · {lobby.grade || "Any grade"} · {lobby.rounds} rounds · {lobby.round_seconds || 120}s / question</div>

        <div className="mt-5">
          <div className="font-mono text-[10px] tracking-[0.3em] text-soft uppercase mb-2">Players ({lobby.players?.length || 0} / {lobby.max_players})</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {(lobby.players || []).map((p) => (
              <div key={p.id} className="rounded-lg border border-white/10 bg-white/[0.03] p-3 flex items-center gap-2">
                {p.id === lobby.host_id && <Crown size={12} className="text-yellow-400" />}
                <div className="font-heading font-bold text-sm truncate flex-1">{p.username}</div>
                {isHost && p.id !== lobby.host_id && lobby.status === "waiting" && (
                  <button
                    onClick={() => setKickTarget(p)}
                    className="text-faint hover:text-rose-400 shrink-0"
                    data-testid="lobby-kick"
                    aria-label={`Remove ${p.username}`}
                    title="Remove player"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {isHost && (lobby.status === "waiting") && (
          <button onClick={makeBracket} disabled={busy || (lobby.players?.length || 0) < 2} className="sm-btn-primary px-5 py-3 mt-6 flex items-center gap-2 disabled:opacity-50" data-testid="lobby-make-bracket">
            <Swords size={14} /> GENERATE BRACKET
          </button>
        )}
      </div>

      {bracket.length > 0 && (
        <div className="sm-card p-6">
          {lobby.champion ? (
            <div className="text-center py-3">
              <Trophy className="w-12 h-12 text-yellow-400 mx-auto" />
              <div className="font-mono text-[10px] tracking-[0.4em] text-blue mt-2 uppercase">Champion</div>
              <div className="font-heading font-black text-3xl mt-1" data-testid="lobby-champion">{lobby.champion}</div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <div className="font-mono text-[10px] tracking-[0.3em] text-blue uppercase">Tournament Bracket</div>
                <h3 className="font-heading font-black text-2xl mt-1">Single Elimination</h3>
              </div>
              {isHost && <div className="font-mono text-[10px] tracking-[0.3em] text-soft uppercase">You are the Host</div>}
            </div>
          )}
          {roundNos.map((rn) => (
            <div key={rn} className="mt-5">
              <div className="font-mono text-[10px] tracking-[0.3em] text-soft uppercase mb-2">Round {rn}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {byRound[rn].map((p) => (
                  <BracketCard key={p.idx} pair={p} isHost={isHost} onStart={() => startPair(p.idx)} busy={busy} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <LeaveConfirmModal
        open={!!kickTarget}
        onCancel={() => setKickTarget(null)}
        onConfirm={doKick}
        title={`Remove ${kickTarget?.username || "player"}?`}
        body="They'll be returned to the home screen. You can regenerate the bracket afterwards."
      />
    </div>
  );
}

function BracketCard({ pair, isHost, onStart, busy }) {
  const status = pair.status || "pending";
  const accent = status === "done" ? "border-emerald-500/40 bg-emerald-500/[0.05]"
                : status === "playing" ? "border-blue-500/55 bg-blue-500/[0.07]"
                : "border-white/10 bg-white/[0.02]";
  return (
    <div className={`rounded-xl border p-4 ${accent}`} data-testid="bracket-card">
      <div className="flex items-center justify-between">
        <div className="font-heading font-bold text-lg">{pair.a || "—"} <span className="text-faint mx-1">vs</span> {pair.b || "BYE"}</div>
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-soft">{pair.bye ? "bye" : status}</div>
      </div>
      {pair.bye && pair.winner && (
        <div className="mt-1 text-xs text-emerald-400 font-bold" data-testid="bracket-bye-note">Advanced to next round (Bye)</div>
      )}
      {!pair.bye && pair.winner && (
        <div className="mt-1 text-xs text-soft">Winner: <span className="text-emerald-400 font-bold">{pair.winner}</span></div>
      )}
      {isHost && status === "pending" && pair.b !== "BYE" && pair.b_id && (
        <button onClick={onStart} disabled={busy} className="sm-btn-primary px-4 py-2 mt-3 flex items-center gap-2 text-xs disabled:opacity-50" data-testid="bracket-start-match">
          <Play size={12} /> START MATCH
        </button>
      )}
    </div>
  );
}

function CustomQuestionsBuilder({ onDone }) {
  const [subject, setSubject] = useState("Math");
  const [topic, setTopic] = useState("Algebra");
  const [grade, setGrade] = useState("Grade 10");
  const [rows, setRows] = useState([{ _k: Math.random(), question: "", answer: "", difficulty: "medium", explanation: "" }]);
  const [busy, setBusy] = useState(false);

  const addRow = () => setRows((r) => [...r, { _k: Math.random(), question: "", answer: "", difficulty: "medium", explanation: "" }]);
  const removeRow = (k) => setRows((r) => r.filter((x) => x._k !== k));
  const updateRow = (k, patch) => setRows((r) => r.map((x) => x._k === k ? { ...x, ...patch } : x));

  const save = async () => {
    const subj = (subject || "").trim();
    const top = (topic || "").trim();
    if (!subj || !top) { toast.error("Subject and Topic are required"); return; }
    const valid = rows.filter((r) => r.question?.trim() && r.answer?.trim());
    if (valid.length === 0) { toast.error("Add at least one question with an answer"); return; }
    setBusy(true);
    try {
      // Include subject/topic on each row so the backend (which validates per-row)
      // doesn't 422 us; also strips the local _k field.
      const payload = {
        subject: subj, topic: top, grade,
        questions: valid.map((r) => ({
          subject: subj, topic: top, grade,
          question: String(r.question || "").trim(),
          answer: String(r.answer || "").trim(),
          difficulty: r.difficulty || "medium",
          explanation: String(r.explanation || "").trim(),
        })),
      };
      const res = await apiClient.post("/custom/batch", payload);
      const inserted = res?.data?.inserted ?? valid.length;
      toast.success(`Saved · ${inserted} question${inserted === 1 ? "" : "s"} added`);
      onDone?.();
    } catch (e) {
      // safeDetail prevents "Objects are not valid as a React child" runtime errors
      // when the backend returns a Pydantic-style list of validation dicts.
      toast.error(safeDetail(e) || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="sm-card p-6 sm:p-8 max-w-4xl">
      <div className="font-mono text-[10px] tracking-[0.3em] text-blue uppercase">Question Set</div>
      <h3 className="font-heading font-black text-2xl mt-1">Author custom questions</h3>
      <div className="text-soft text-sm mt-2">Save your set and reuse it in any private match. Subject + topic apply to all rows.</div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label="Subject"><input className="sm-input" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={32} data-testid="cq-subject" /></Field>
        <Field label="Topic"><input className="sm-input" value={topic} onChange={(e) => setTopic(e.target.value)} maxLength={48} data-testid="cq-topic" /></Field>
        <Field label="Grade">
          <select className="sm-input" value={grade} onChange={(e) => setGrade(e.target.value)} data-testid="cq-grade">
            {GRADES.map((g) => <option key={g}>{g}</option>)}
          </select>
        </Field>
      </div>

      <div className="mt-6 space-y-3">
        <AnimatePresence initial={false}>
          {rows.map((r, i) => (
            <motion.div key={r._k} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
              <div className="flex items-center justify-between">
                <div className="font-mono text-[10px] tracking-[0.25em] text-soft uppercase">Question #{i + 1}</div>
                <button onClick={() => removeRow(r._k)} className="text-faint hover:text-rose-400" data-testid={`cq-remove-${i}`}><Trash2 size={14} /></button>
              </div>
              <textarea className="sm-input mt-2" rows={2} placeholder="Question text" value={r.question} onChange={(e) => updateRow(r._k, { question: e.target.value })} data-testid={`cq-q-${i}`} />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                <input className="sm-input" placeholder="Answer" value={r.answer} onChange={(e) => updateRow(r._k, { answer: e.target.value })} data-testid={`cq-a-${i}`} />
                <select className="sm-input" value={r.difficulty} onChange={(e) => updateRow(r._k, { difficulty: e.target.value })} data-testid={`cq-d-${i}`}>
                  <option value="easy">Easy</option><option value="medium">Medium</option><option value="hard">Hard</option>
                </select>
                <input className="sm-input" placeholder="Explanation (optional)" value={r.explanation} onChange={(e) => updateRow(r._k, { explanation: e.target.value })} data-testid={`cq-e-${i}`} />
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        <button onClick={addRow} className="sm-btn-outline px-4 py-2 text-xs flex items-center gap-2" data-testid="cq-add-row">
          <Plus size={12} /> ADD QUESTION
        </button>
      </div>

      <div className="mt-7 flex items-center justify-end gap-3">
        <button onClick={onDone} className="sm-btn-outline px-5 py-3" data-testid="cq-cancel">CANCEL</button>
        <button onClick={save} disabled={busy} className="sm-btn-primary px-5 py-3 flex items-center gap-2 disabled:opacity-50" data-testid="cq-save">
          <Save size={14} /> {busy ? "SAVING…" : "SAVE QUESTION SET"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="font-mono text-[10px] tracking-[0.3em] text-soft uppercase mb-2">{label}</div>
      {children}
    </label>
  );
}
